import { TARGET_ACTIVE_COLOR, TARGET_INACTIVE_COLOR, layerTargets } from 'utilities/targetShapes';
import './BallTrajectory3DChart.scss';

import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { t } from 'utilities/TranslationUtils';
import { KICK_RESULT_BUCKET_COLORS, kickResultBucket } from 'utilities/chartUtils';
import { BallTrajectoryData, OtherTrajectory } from 'types/models/BallTrajectory';
import { WallPlayerGeo, TargetShapeGeo } from 'types/models/SessionEnrichment';
import { evalPoly, toScene, sampleTrajectory, positionAtTime, totalDuration } from 'utilities/trajectoryUtils';

export type { TrajectorySegment, BallTrajectoryData, OtherTrajectory } from 'types/models/BallTrajectory';

// ── Soccer pitch dimensions (metres) ──
const GOAL_WIDTH = 7.32;
const GOAL_HEIGHT = 2.44;
const GOAL_DEPTH = 1.8;
const PENALTY_AREA_DEPTH = 16.5;
const PENALTY_AREA_WIDTH = 40.32;
const GOAL_AREA_DEPTH = 5.5;
const GOAL_AREA_WIDTH = 18.32;
const PENALTY_SPOT_X = 11.0;
const PITCH_EXTENT_X = 52;
const PITCH_EXTENT_Z = 34;
// Full pitch (both halves): goal line to goal line is ~105 m; halfway line + centre circle at the middle.
const FULL_LENGTH = 105;
const HALF_LENGTH = FULL_LENGTH / 2;
const CENTER_CIRCLE_RADIUS = 9.15;

type Props = {
  /** Optional injected trajectory; falls back to a hardcoded sample when omitted. */
  trajectory?: BallTrajectoryData | null;
  /** Other trajectories from the same session, drawn faint when the gear toggle is on. */
  otherTrajectories?: OtherTrajectory[];
  /** Reachable-region segment overlays in soccer coords [x, z], colored by OnTarget.
   *  Only segments with `isActive: true` are used to color the trajectory tube;
   *  all segments get a ground shadow regardless. */
  reachableRegionSegments?: Array<{
    id: string;
    sessionId?: string;
    start: [number, number];
    end: [number, number];
    startY?: number | null;
    endY?: number | null;
    startTime?: number | null;
    endTime?: number | null;
    onTarget: number | null;
    /** True for the segment belonging to the currently-active (primary) kick. */
    isActive?: boolean;
  }>;
  /** Free-kick wall players (SharedCalculator quads) drawn as simple yellow mannequins on the pitch. */
  wallPlayers?: WallPlayerGeo[];
  /** Session targets (goalkeeper circles / corner points) drawn on the pitch. */
  targets?: TargetShapeGeo[];
  /** Called when the user clicks one of the sibling trajectories. */
  onSelectOther?: (sessionId: string, measurementId: string) => void;
  /** Whether sibling trajectories should be visible on first render. */
  initialShowOthers?: boolean;
  /** Hides camera presets, orbit toggle, fly-mode, legend, and hint — keeps only settings + fullscreen. */
  compact?: boolean;
  /** Camera preset the view opens with (default: behind the kick location). */
  defaultCameraPreset?: CameraPreset;
};

export type CameraPreset = 'kickLocation' | 'penaltyBox' | 'goalkeeper';
const CAMERA_PRESERVE_DISTANCE_M = 2;
const CAMERA_MIN_Y_M = 0.05;
const FLY_MOVE_SPEED_MPS = 14.3;
const FLY_MOUSE_LOOK_SENSITIVITY = 0.0032;
const FLY_KEY_LOOK_SPEED_RAD_PER_SEC = 1.7;

const BallTrajectory3DChart = ({ trajectory, otherTrajectories, reachableRegionSegments, wallPlayers, targets, onSelectOther, initialShowOthers = false, compact = false, defaultCameraPreset = 'kickLocation' }: Props): React.ReactElement => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [speed, setSpeed] = useState<number>(1);
  const [showNet, setShowNet] = useState(true);
  const [showOthers, setShowOthers] = useState(initialShowOthers);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cameraPreset, setCameraPreset] = useState<CameraPreset>(defaultCameraPreset);
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** Current playback time (seconds). Reported up from the animation loop for the scrubber. */
  const [playbackTime, setPlaybackTime] = useState(0);
  /** Total flight duration (seconds), known once the scene is built. */
  const [duration, setDuration] = useState(0);

  // Refs to mutable controls so the animation loop reads the latest values
  // without needing to re-create the scene.
  const isPlayingRef = useRef(isPlaying);
  const speedRef = useRef(speed);
  /**
   * Seek requests are written to this ref by the slider and consumed (cleared) by the
   * animation loop on the next frame. Using a ref avoids re-creating the scene when the
   * user scrubs.
   */
  const seekRef = useRef<number | null>(null);
  /** Ref to the goal-net group so the gear panel can toggle visibility cleanly. */
  const netGroupRef = useRef<THREE.Group | null>(null);
  /** Ref to the group containing all sibling-session trajectory tubes. */
  const othersGroupRef = useRef<THREE.Group | null>(null);
  /** Mirror of `showOthers` accessed by the animation loop without re-running the effect. */
  const showOthersRef = useRef(false);
  /** Latest click handler exposed to the animation loop without re-running the effect. */
  const onSelectOtherRef = useRef<Props['onSelectOther']>(undefined);
  /** Persisted camera pose between kick switches to avoid jumpy recentering on near-identical kicks. */
  const cameraMemoryRef = useRef<{
    position: THREE.Vector3;
    quaternion: THREE.Quaternion;
    target: THREE.Vector3;
    kickLocation: { x: number; z: number };
    cameraPreset: CameraPreset;
  } | null>(null);
  useEffect(() => { onSelectOtherRef.current = onSelectOther; }, [onSelectOther]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === rootRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    onFullscreenChange();
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  // Toggle net visibility without rebuilding the scene.
  useEffect(() => {
    if (netGroupRef.current) netGroupRef.current.visible = showNet;
  }, [showNet]);

  // Toggle other-trajectory visibility without rebuilding the scene.
  useEffect(() => {
    showOthersRef.current = showOthers;
    if (othersGroupRef.current) othersGroupRef.current.visible = showOthers;
  }, [showOthers]);

  useEffect(() => {
    if (!trajectory) return;
    const traj = trajectory;
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const height = mount.clientHeight;

    // ── Scene / renderer ──
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0b1020');
    scene.fog = new THREE.Fog('#0b1020', 60, 160);

    const camera = new THREE.PerspectiveCamera(55, width / height, 0.1, 500);
    camera.position.copy(toScene(28, 5, 6));

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.sortObjects = true;
    renderer.domElement.tabIndex = 0;
    mount.appendChild(renderer.domElement);

    // ── Lighting ──
    scene.add(new THREE.HemisphereLight('#aac4ff', '#1a3320', 0.55));
    scene.add(new THREE.AmbientLight('#ffffff', 0.25));
    const sun = new THREE.DirectionalLight('#fff6d8', 1.1);
    sun.position.set(20, 40, 30);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 120;
    scene.add(sun);

    // ── Pitch ground ──
    const pitch = new THREE.Mesh(
      new THREE.PlaneGeometry(PITCH_EXTENT_Z * 2, FULL_LENGTH),
      new THREE.MeshStandardMaterial({ color: '#2a5c2a', roughness: 0.95, metalness: 0 }),
    );
    pitch.rotation.x = -Math.PI / 2;
    pitch.position.copy(toScene(HALF_LENGTH, 0, 0));
    pitch.receiveShadow = true;
    pitch.renderOrder = 0;
    scene.add(pitch);

    // Mowing stripes
    const stripeMat = new THREE.MeshStandardMaterial({ 
      color: '#23501f', roughness: 0.95, transparent: true, opacity: 0.55,
      polygonOffset: true,
      polygonOffsetUnits: 1,
    });
    const stripeWidth = 4;
    for (let i = 0; i < FULL_LENGTH; i += stripeWidth * 2) {
      const stripe = new THREE.Mesh(new THREE.PlaneGeometry(PITCH_EXTENT_Z * 2, stripeWidth), stripeMat);
      stripe.rotation.x = -Math.PI / 2;
      stripe.position.copy(toScene(i + stripeWidth / 2, 0.001, 0));
      stripe.renderOrder = 1;
      scene.add(stripe);
    }

    // ── Pitch line markings (full pitch, both halves) ──
    const lineMat = new THREE.LineBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.85 });
    function lineSegmentsFromSoccerPoints(points: { x: number; z: number }[]): THREE.Line {
      const pts = points.map((p) => toScene(p.x, 0.01, p.z));
      return new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), lineMat);
    }
    const whiteSpotMat = new THREE.MeshBasicMaterial({ color: '#ffffff' });
    const addSpot = (x: number) => {
      const spot = new THREE.Mesh(new THREE.CircleGeometry(0.15, 24), whiteSpotMat);
      spot.rotation.x = -Math.PI / 2;
      spot.position.copy(toScene(x, 0.01, 0));
      spot.renderOrder = 2;
      scene.add(spot);
    };

    // Touchlines, both goal lines, halfway line.
    scene.add(lineSegmentsFromSoccerPoints([{ x: 0, z: -PITCH_EXTENT_Z }, { x: FULL_LENGTH, z: -PITCH_EXTENT_Z }]));
    scene.add(lineSegmentsFromSoccerPoints([{ x: 0, z: PITCH_EXTENT_Z }, { x: FULL_LENGTH, z: PITCH_EXTENT_Z }]));
    scene.add(lineSegmentsFromSoccerPoints([{ x: 0, z: -PITCH_EXTENT_Z }, { x: 0, z: PITCH_EXTENT_Z }]));
    scene.add(lineSegmentsFromSoccerPoints([{ x: FULL_LENGTH, z: -PITCH_EXTENT_Z }, { x: FULL_LENGTH, z: PITCH_EXTENT_Z }]));
    scene.add(lineSegmentsFromSoccerPoints([{ x: HALF_LENGTH, z: -PITCH_EXTENT_Z }, { x: HALF_LENGTH, z: PITCH_EXTENT_Z }]));

    // Centre circle + centre spot.
    {
      const ccPts: THREE.Vector3[] = [];
      const segs = 72;
      for (let i = 0; i <= segs; i++) {
        const a = (i / segs) * Math.PI * 2;
        ccPts.push(toScene(HALF_LENGTH + Math.cos(a) * CENTER_CIRCLE_RADIUS, 0.01, Math.sin(a) * CENTER_CIRCLE_RADIUS));
      }
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(ccPts), lineMat));
      addSpot(HALF_LENGTH);
    }

    // Penalty area, goal area, penalty spot and arc — mirrored at each end.
    // dir = +1 draws into the field from the near goal line (x=0); dir = -1 from the far line (x=FULL_LENGTH).
    const buildEndMarkings = (goalLineX: number, dir: 1 | -1) => {
      scene.add(lineSegmentsFromSoccerPoints([
        { x: goalLineX, z: -PENALTY_AREA_WIDTH / 2 },
        { x: goalLineX + dir * PENALTY_AREA_DEPTH, z: -PENALTY_AREA_WIDTH / 2 },
        { x: goalLineX + dir * PENALTY_AREA_DEPTH, z: PENALTY_AREA_WIDTH / 2 },
        { x: goalLineX, z: PENALTY_AREA_WIDTH / 2 },
      ]));
      scene.add(lineSegmentsFromSoccerPoints([
        { x: goalLineX, z: -GOAL_AREA_WIDTH / 2 },
        { x: goalLineX + dir * GOAL_AREA_DEPTH, z: -GOAL_AREA_WIDTH / 2 },
        { x: goalLineX + dir * GOAL_AREA_DEPTH, z: GOAL_AREA_WIDTH / 2 },
        { x: goalLineX, z: GOAL_AREA_WIDTH / 2 },
      ]));
      const spotX = goalLineX + dir * PENALTY_SPOT_X;
      addSpot(spotX);
      const arcPoints: THREE.Vector3[] = [];
      const arcRadius = 9.15;
      const areaLineX = goalLineX + dir * PENALTY_AREA_DEPTH;
      for (let a = -Math.PI / 2.4; a <= Math.PI / 2.4; a += 0.05) {
        const x = spotX + dir * Math.cos(a) * arcRadius;
        const z = Math.sin(a) * arcRadius;
        if (dir > 0 ? x > areaLineX : x < areaLineX) arcPoints.push(toScene(x, 0.01, z));
      }
      if (arcPoints.length > 1) scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(arcPoints), lineMat));
    };
    buildEndMarkings(0, 1);
    buildEndMarkings(FULL_LENGTH, -1);

    // ── Goal frames (posts + net) at both ends ──
    const halfW = GOAL_WIDTH / 2;
    const postRadius = 0.06;
    const postMat = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.4, metalness: 0.3 });
    function addBar(start: THREE.Vector3, end: THREE.Vector3) {
      const dir = new THREE.Vector3().subVectors(end, start);
      const len = dir.length();
      const bar = new THREE.Mesh(new THREE.CylinderGeometry(postRadius, postRadius, len, 12), postMat);
      bar.castShadow = true;
      bar.position.copy(start.clone().add(end).multiplyScalar(0.5));
      bar.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
      scene.add(bar);
    }

    const netGroup = new THREE.Group();
    netGroup.name = 'net';
    netGroup.visible = showNet;
    scene.add(netGroup);
    netGroupRef.current = netGroup;
    const netMat = new THREE.LineBasicMaterial({ color: '#cccccc', transparent: true, opacity: 0.35 });
    function addNetGrid(corners: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3], divsU = 14, divsV = 6) {
      const [a, b, , d] = corners;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= divsV; i++) {
        const f = i / divsV;
        pts.push(a.clone().lerp(d, f), b.clone().lerp(corners[2], f));
      }
      for (let i = 0; i <= divsU; i++) {
        const f = i / divsU;
        pts.push(a.clone().lerp(b, f), d.clone().lerp(corners[2], f));
      }
      netGroup.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), netMat));
    }
    // dir = +1: net box sits behind the near goal line (toward -x); dir = -1: behind the far line (toward +x).
    const buildGoalFrame = (goalLineX: number, dir: 1 | -1) => {
      const backX = goalLineX - dir * GOAL_DEPTH;
      addBar(toScene(goalLineX, 0, -halfW), toScene(goalLineX, GOAL_HEIGHT, -halfW));
      addBar(toScene(goalLineX, 0, halfW), toScene(goalLineX, GOAL_HEIGHT, halfW));
      addBar(toScene(goalLineX, GOAL_HEIGHT, -halfW), toScene(goalLineX, GOAL_HEIGHT, halfW));
      addBar(toScene(backX, 0, -halfW), toScene(backX, GOAL_HEIGHT, -halfW));
      addBar(toScene(backX, 0, halfW), toScene(backX, GOAL_HEIGHT, halfW));
      addBar(toScene(backX, GOAL_HEIGHT, -halfW), toScene(backX, GOAL_HEIGHT, halfW));
      addBar(toScene(goalLineX, GOAL_HEIGHT, -halfW), toScene(backX, GOAL_HEIGHT, -halfW));
      addBar(toScene(goalLineX, GOAL_HEIGHT, halfW), toScene(backX, GOAL_HEIGHT, halfW));
      addBar(toScene(goalLineX, 0, -halfW), toScene(backX, 0, -halfW));
      addBar(toScene(goalLineX, 0, halfW), toScene(backX, 0, halfW));
      addNetGrid([toScene(backX, 0, -halfW), toScene(backX, 0, halfW), toScene(backX, GOAL_HEIGHT, halfW), toScene(backX, GOAL_HEIGHT, -halfW)]);
      addNetGrid([toScene(goalLineX, GOAL_HEIGHT, -halfW), toScene(goalLineX, GOAL_HEIGHT, halfW), toScene(backX, GOAL_HEIGHT, halfW), toScene(backX, GOAL_HEIGHT, -halfW)], 14, 4);
      addNetGrid([toScene(goalLineX, 0, -halfW), toScene(backX, 0, -halfW), toScene(backX, GOAL_HEIGHT, -halfW), toScene(goalLineX, GOAL_HEIGHT, -halfW)], 4, 6);
      addNetGrid([toScene(goalLineX, 0, halfW), toScene(backX, 0, halfW), toScene(backX, GOAL_HEIGHT, halfW), toScene(goalLineX, GOAL_HEIGHT, halfW)], 4, 6);
    };
    buildGoalFrame(0, 1);
    buildGoalFrame(FULL_LENGTH, -1);

    // ── Trajectory tube ──
    // CatmullRomCurve3 / TubeGeometry crash with cryptic errors when given < 2 points;
    // bail out cleanly so the pitch and goal still render for untracked measurements.
    const samples = sampleTrajectory(traj, 200);
    if (samples.length < 2) {
      setDuration(0);
      renderer.render(scene, camera);
      const ro0 = new ResizeObserver(() => {
        const w = mount.clientWidth;
        const h = mount.clientHeight;
        camera.aspect = w / h;
        camera.updateProjectionMatrix();
        renderer.setSize(w, h);
        renderer.render(scene, camera);
      });
      ro0.observe(mount);
      return () => {
        ro0.disconnect();
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    }
    // ── Reachable-region helpers (needed before tube split) ──
    const onTargetColor = (value: number | null | undefined): THREE.Color => {
      const v = Math.max(0, Math.min(1, value ?? 0));
      const r = Math.round(255 + (34 - 255) * v);
      const g = Math.round(255 + (197 - 255) * v);
      const b = Math.round(255 + (94 - 255) * v);
      return new THREE.Color(r / 255, g / 255, b / 255);
    };

    const samplePositions = samples.map((s) => s.pos);

    const isFiniteNumber = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);
    const hasValidReachWindow = (seg: { startTime?: number | null; endTime?: number | null }): seg is { startTime: number; endTime: number } => (
      isFiniteNumber(seg.startTime) && isFiniteNumber(seg.endTime)
    );

    const nearestSampleIndexForTime = (times: number[], targetTime: number): number => {
      if (times.length === 0) return 0;
      let bestIdx = 0;
      let bestDt = Math.abs(times[0] - targetTime);
      for (let i = 1; i < times.length; i++) {
        const dt = Math.abs(times[i] - targetTime);
        if (dt < bestDt) {
          bestDt = dt;
          bestIdx = i;
        }
      }
      return bestIdx;
    };

    // Build reachable ranges for a given set of sample positions + matching segments.
    type ReachRange = { lo: number; hi: number; col: THREE.Color };
    const buildReachRanges = (
      samplePoints: { t: number; pos: THREE.Vector3 }[],
      segs: NonNullable<typeof reachableRegionSegments>,
    ): ReachRange[] => {
      const times = samplePoints.map((p) => p.t);
      const ranges: ReachRange[] = [];
      for (const seg of segs) {
        if (!hasValidReachWindow(seg)) continue;
        const lo = nearestSampleIndexForTime(times, seg.startTime);
        const hi = nearestSampleIndexForTime(times, seg.endTime);
        const rangeLo = Math.min(lo, hi);
        const rangeHi = Math.max(lo, hi);
        if (rangeHi > rangeLo) ranges.push({ lo: rangeLo, hi: rangeHi, col: onTargetColor(seg.onTarget) });
      }
      ranges.sort((a, b) => a.lo - b.lo);
      return ranges;
    };

    const reachRanges = buildReachRanges(
      samples,
      (reachableRegionSegments ?? []).filter((s) => s.isActive && hasValidReachWindow(s)),
    );

    // ── Trajectory tube + selected reachable overlays ──
    // renderOrder > stripes (1) so tubes always composite after pitch geometry.
    const TUBE_RENDER_ORDER = 5;
    const addTubeSegment = (pts: THREE.Vector3[], mat: THREE.Material, radius = 0.065): void => {
      if (pts.length < 2) return;
      const c = new THREE.CatmullRomCurve3(pts);
      const mesh = new THREE.Mesh(new THREE.TubeGeometry(c, Math.max(8, pts.length * 2), radius, 8, false), mat);
      mesh.renderOrder = TUBE_RENDER_ORDER;
      scene.add(mesh);
    };

    // The selected kick's tube takes its result colour (amber only when the caller gives none).
    const mainColor = trajectory?.color ?? '#ffb800';
    const orangeMat = new THREE.MeshStandardMaterial({
      color: mainColor, emissive: trajectory?.color ?? '#ff9500', emissiveIntensity: 0.4,
      roughness: 0.5, metalness: 0.1, transparent: true, opacity: 0.85,
      depthWrite: false,
    });

    addTubeSegment(samplePositions, orangeMat, 0.065);
    for (const r of reachRanges) {
      const reachMat = new THREE.MeshStandardMaterial({
        color: r.col, emissive: r.col, emissiveIntensity: 0.35,
        roughness: 0.4, metalness: 0.05, transparent: true, opacity: 0.97,
        depthWrite: false,
      });
      addTubeSegment(samplePositions.slice(r.lo, r.hi + 1), reachMat, 0.0741);
    }

    // ── Reachable-region ground overlays (flat rectangular projection strips) ──
    type ReachableShadowEntry = {
      mesh: THREE.Mesh;
      material: THREE.MeshBasicMaterial;
      kickId: string;
      sessionId?: string;
      defaultOpacity: number;
    };
    const reachableShadowEntries: ReachableShadowEntry[] = [];
    const SHADOW_DEFAULT_OPACITY = 0.74;
    const SHADOW_HOVER_OPACITY = 0.95;
    const SHADOW_HEIGHT = 0.08;
    for (const seg of (reachableRegionSegments ?? []).filter((s) => hasValidReachWindow(s))) {
      const col = onTargetColor(seg.onTarget);
      const groundStart = toScene(seg.start[0], SHADOW_HEIGHT, seg.start[1]);
      const groundEnd = toScene(seg.end[0], SHADOW_HEIGHT, seg.end[1]);
      const dir = new THREE.Vector3().subVectors(groundEnd, groundStart);
      const len = dir.length();
      if (len < 1e-4) continue;

      // Build a flat quad aligned with the segment on the XZ plane.
      dir.normalize();
      const right = new THREE.Vector3(-dir.z, 0, dir.x);
      const halfWidth = 0.195;
      const p1 = groundStart.clone().addScaledVector(right, halfWidth);
      const p2 = groundEnd.clone().addScaledVector(right, halfWidth);
      const p3 = groundEnd.clone().addScaledVector(right, -halfWidth);
      const p4 = groundStart.clone().addScaledVector(right, -halfWidth);

      const groundGeometry = new THREE.BufferGeometry();
      groundGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
        p1.x, p1.y, p1.z,
        p2.x, p2.y, p2.z,
        p3.x, p3.y, p3.z,
        p1.x, p1.y, p1.z,
        p3.x, p3.y, p3.z,
        p4.x, p4.y, p4.z,
      ], 3));
      groundGeometry.computeVertexNormals();

      const groundMat = new THREE.MeshBasicMaterial({
        color: col,
        transparent: true,
        opacity: SHADOW_DEFAULT_OPACITY,
        side: THREE.DoubleSide,
        depthWrite: false,
      });
      const groundMesh = new THREE.Mesh(groundGeometry, groundMat);
      groundMesh.userData.kickId = seg.id;
      groundMesh.userData.sessionId = seg.sessionId;
      groundMesh.renderOrder = TUBE_RENDER_ORDER;
      scene.add(groundMesh);
      reachableShadowEntries.push({
        mesh: groundMesh,
        material: groundMat,
        kickId: seg.id,
        sessionId: seg.sessionId,
        defaultOpacity: SHADOW_DEFAULT_OPACITY,
      });
    }

    // ── Sibling trajectories ──
    const othersGroup = new THREE.Group();
    othersGroup.name = 'others';
    othersGroup.visible = showOthersRef.current;
    scene.add(othersGroup);
    othersGroupRef.current = othersGroup;

    type OtherEntry = {
      id: string;
      baseMesh: THREE.Mesh;
      reachableMeshes: THREE.Mesh[];
      allMeshes: THREE.Mesh[];
      material: THREE.MeshStandardMaterial;
      defaultOpacity: number;
    };
    const otherEntries: OtherEntry[] = [];
    const OTHER_DEFAULT_OPACITY = 0.45;
    const OTHER_HOVER_OPACITY = 0.98;

    for (const o of otherTrajectories ?? []) {
const oSamples = sampleTrajectory(o.data, 100);
      if (oSamples.length < 2) continue;
      const oPositions = oSamples.map((s) => s.pos);
      // Same colour as the kick's chip and markers; callers pass it, else derive it from the result's bucket.
      const colorHex = o.color ?? KICK_RESULT_BUCKET_COLORS[kickResultBucket(o.kickResult)];
      const oMat = new THREE.MeshStandardMaterial({
        color: colorHex, emissive: colorHex, emissiveIntensity: 0.15,
        roughness: 0.6, metalness: 0.05, transparent: true,
        opacity: OTHER_DEFAULT_OPACITY, depthWrite: false,
      });
      const oMeshBase = new THREE.Mesh(
        new THREE.TubeGeometry(new THREE.CatmullRomCurve3(oPositions), Math.max(48, oSamples.length), 0.052, 6, false),
        oMat,
      );
      oMeshBase.userData.kickId = o.id;
      oMeshBase.userData.sessionId = o.sessionId;
      oMeshBase.renderOrder = TUBE_RENDER_ORDER;
      othersGroup.add(oMeshBase);

      const reachableMeshes: THREE.Mesh[] = [];
      const allMeshes: THREE.Mesh[] = [oMeshBase];

      // Reachable region overlays for this sibling (always visible when siblings shown)
      const sibSegs = (reachableRegionSegments ?? []).filter((s) => s.id === o.id && !s.isActive && hasValidReachWindow(s));
      if (sibSegs.length > 0) {
        for (const r of buildReachRanges(oSamples, sibSegs)) {
          const pts = oPositions.slice(r.lo, r.hi + 1);
          if (pts.length < 2) continue;
          const rMat = new THREE.MeshStandardMaterial({
            color: r.col, emissive: r.col, emissiveIntensity: 0.4,
            roughness: 0.4, metalness: 0.05, transparent: true,
            opacity: OTHER_DEFAULT_OPACITY * 1.4, depthWrite: false,
          });
          const rMesh = new THREE.Mesh(
            new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), Math.max(8, pts.length * 2), 0.065, 8, false),
            rMat,
          );
          rMesh.userData.kickId = o.id;
          rMesh.userData.sessionId = o.sessionId;
          rMesh.visible = false;
          rMesh.renderOrder = TUBE_RENDER_ORDER;
          othersGroup.add(rMesh);
          reachableMeshes.push(rMesh);
          allMeshes.push(rMesh);
        }
      }

      otherEntries.push({
        id: o.id,
        baseMesh: oMeshBase,
        reachableMeshes,
        allMeshes,
        material: oMat,
        defaultOpacity: OTHER_DEFAULT_OPACITY,
      });
    }

    // ── Pointer / click tracking for sibling hover highlight ──
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let pointerInside = false;
    const onPointerMove = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      pointerInside = true;
    };
    const onPointerLeave = () => { pointerInside = false; };
    const onClick = (e: MouseEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);

      const clickableMeshes: THREE.Object3D[] = [];
      // Trajectory tubes are clickable only while they are visible.
      if (showOthersRef.current && otherEntries.length > 0) {
        clickableMeshes.push(...otherEntries.map((en) => en.baseMesh));
      }
      // Reachable shadow projections are always clickable.
      if (reachableShadowEntries.length > 0) {
        clickableMeshes.push(...reachableShadowEntries.map((en) => en.mesh));
      }

      if (clickableMeshes.length === 0) return;
      const hits = raycaster.intersectObjects(clickableMeshes, false);
      if (hits.length === 0) return;
      const hit = hits[0].object;
      const kickId = hit.userData.kickId as string | undefined;
      const sessionId = hit.userData.sessionId as string | undefined;
      if (kickId && sessionId) onSelectOtherRef.current?.(sessionId, kickId);
    };
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    renderer.domElement.addEventListener('click', onClick);

    // ── Ground shadow trail ──
    const groundSamples = samples.map((s) => new THREE.Vector3(s.pos.x, 0.08, s.pos.z));
    const groundCurve = new THREE.CatmullRomCurve3(groundSamples);
    const groundTrailMesh = new THREE.Mesh(
      new THREE.TubeGeometry(groundCurve, Math.max(64, samples.length), 0.065, 8, false),
      new THREE.MeshBasicMaterial({ color: '#000000', transparent: true, opacity: 0.65, depthWrite: false }),
    );
    groundTrailMesh.renderOrder = TUBE_RENDER_ORDER;
    scene.add(groundTrailMesh);

    // ── Animated ball ──
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.11, 24, 24),
      new THREE.MeshStandardMaterial({ color: '#ffffff', emissive: '#ffe680', emissiveIntensity: 0.4, roughness: 0.4 }),
    );
    ball.castShadow = true;
    scene.add(ball);

    // ── Markers: kick location ring and goal-crossing ring ──
    const kickPos = toScene(traj.kickLocation.x, 0, traj.kickLocation.z);
    {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.45, 0.6, 48),
        new THREE.MeshBasicMaterial({ color: '#00e5ff', side: THREE.DoubleSide, transparent: true, opacity: 0.9 }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.copy(kickPos.clone().setY(0.02));
      ring.renderOrder = TUBE_RENDER_ORDER + 1;
      scene.add(ring);
    }
    {
      const cross = traj.goalCrossing ? toScene(0, traj.goalCrossing.y, traj.goalCrossing.z) : null;
      if (cross) {
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(0.18, 0.24, 32),
          new THREE.MeshBasicMaterial({ color: '#00ff88', side: THREE.DoubleSide, transparent: true, opacity: 0.95 }),
        );
        ring.lookAt(kickPos);
        ring.position.copy(cross);
        ring.renderOrder = TUBE_RENDER_ORDER + 1;
        scene.add(ring);
        const dot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 16, 16), new THREE.MeshBasicMaterial({ color: '#00ff88' }));
        dot.position.copy(cross);
        dot.renderOrder = TUBE_RENDER_ORDER + 1;
        scene.add(dot);
      }
    }

    // ── Targets (goalkeeper circles / corner points) drawn flat on the pitch ──
    // The aimed-at target (isActive) is yellow; the rest are drawn gray.
    const TARGET_RENDER_ORDER = TUBE_RENDER_ORDER + 2;
    const layeredTargets = layerTargets(targets);

    for (let targetIndex = 0; targetIndex < layeredTargets.length; targetIndex += 1) {
      const target = layeredTargets[targetIndex];
      const targetColor = target.isActive ? TARGET_ACTIVE_COLOR : TARGET_INACTIVE_COLOR;
      const [tx, ty, tz] = target.center;
      const layerLift = targetIndex * 0.003;
      const center = toScene(tx, (ty ?? 0) + 0.02 + layerLift, tz);
      if (target.shape === 'circle') {
        const inner = Math.max(0, target.innerRadius ?? 0);
        const outer = Math.max(inner + 0.1, target.outerRadius ?? inner + 0.3);
        // Inner: filled coloured disc.
        if (inner > 0) {
          const disc = new THREE.Mesh(
            new THREE.CircleGeometry(inner, 48),
            new THREE.MeshBasicMaterial({
              color: targetColor,
              side: THREE.DoubleSide,
              transparent: true,
              opacity: 0.35,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -1,
              polygonOffsetUnits: -(targetIndex + 1),
            }),
          );
          disc.rotation.x = -Math.PI / 2;
          disc.position.copy(center);
          disc.renderOrder = TARGET_RENDER_ORDER + targetIndex * 3;
          scene.add(disc);
        }
        // Outer: dotted circle outline.
        const dashPts: THREE.Vector3[] = [];
        const segs = 72;
        for (let i = 0; i <= segs; i++) {
          const a = (i / segs) * Math.PI * 2;
          dashPts.push(new THREE.Vector3(Math.cos(a) * outer, 0, Math.sin(a) * outer));
        }
        const outerLine = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(dashPts),
          new THREE.LineDashedMaterial({ color: targetColor, dashSize: 0.3, gapSize: 0.22, transparent: true, opacity: 0.95 }),
        );
        outerLine.computeLineDistances();
        outerLine.position.copy(center);
        outerLine.renderOrder = TARGET_RENDER_ORDER + targetIndex * 3 + 1;
        scene.add(outerLine);
      } else if (target.shape === 'rectangle') {
        const rect = new THREE.Mesh(
          // PlaneGeometry lies in XY then rotates to XZ: width = lateral (z), height = depth (x).
          new THREE.PlaneGeometry(Math.max(0.1, target.zLength ?? 1), Math.max(0.1, target.xLength ?? 1)),
          new THREE.MeshBasicMaterial({
            color: targetColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -(targetIndex + 1),
          }),
        );
        rect.rotation.x = -Math.PI / 2;
        rect.position.copy(center);
        rect.renderOrder = TARGET_RENDER_ORDER + targetIndex * 3;
        scene.add(rect);
      } else {
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(0.16, 0.22, 24),
          new THREE.MeshBasicMaterial({
            color: targetColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.85,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -(targetIndex + 1),
          }),
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.copy(center);
        ring.renderOrder = TARGET_RENDER_ORDER + targetIndex * 3 + 1;
        scene.add(ring);
      }
    }

    // ── Free-kick wall: a simple yellow mannequin per defender (a 3D figure, not a billboard) ──
    // Each figure stands at the CENTRE of its SharedCalculator quad, so slim figures leave gaps and
    // don't overlap; the quad's vertical extent sets the figure's height (near/far players differ).
    if ((wallPlayers?.length ?? 0) > 0) {
      // Matte yellow, low sheen ("less shader"): flat-ish standard material, no emissive.
      const wallMat = new THREE.MeshStandardMaterial({
        color: '#e6b800', metalness: 0.0, roughness: 0.85, transparent: true, opacity: 0.8,
      });
      // A blocky training-dummy shape (not a human figure): a wide, thin slab body on a base
      // with a raised boxy head. Wider (lateral) than thick (depth) so it reads as a wall panel.
      const buildMannequin = (height: number): THREE.Group => {
        const g = new THREE.Group();
        const width = 0.62;   // lateral (across the wall) — wider
        const depth = 0.24;   // toward goal — thinner
        const baseH = 0.06;
        const headH = 0.3;
        const headW = 0.3;
        const neckGap = 0.14;  // lifts the head clear of the body so it sits higher
        const torsoLen = Math.max(0.45, height - baseH - neckGap - headH);
        const bottomScale = 0.72; // slab narrows to 72% width at the foundation, full width up top
        const base = new THREE.Mesh(new THREE.BoxGeometry(width * bottomScale + 0.06, baseH, depth + 0.1), wallMat);
        base.position.y = baseH / 2;
        base.castShadow = true;
        g.add(base);
        // Taper the slab so it is a bit less wide toward the bottom (a trapezoid prism, still blocky).
        const torsoGeo = new THREE.BoxGeometry(width, torsoLen, depth);
        const tp = torsoGeo.attributes.position;
        for (let i = 0; i < tp.count; i++) {
          const frac = (tp.getY(i) + torsoLen / 2) / torsoLen; // 0 bottom → 1 top
          const s = bottomScale + (1 - bottomScale) * frac;
          tp.setX(i, tp.getX(i) * s);
          tp.setZ(i, tp.getZ(i) * s);
        }
        torsoGeo.computeVertexNormals();
        const torso = new THREE.Mesh(torsoGeo, wallMat);
        torso.position.y = baseH + torsoLen / 2;
        torso.castShadow = true;
        g.add(torso);
        const head = new THREE.Mesh(new THREE.BoxGeometry(headW, headH, depth * 0.92), wallMat);
        head.position.y = baseH + torsoLen + neckGap + headH / 2;
        head.castShadow = true;
        g.add(head);
        return g;
      };
      for (const wp of wallPlayers ?? []) {
        const ll = toScene(wp.lowerLeft[0], wp.lowerLeft[1], wp.lowerLeft[2]);
        const lr = toScene(wp.lowerRight[0], wp.lowerRight[1], wp.lowerRight[2]);
        const ul = toScene(wp.upperLeft[0], wp.upperLeft[1], wp.upperLeft[2]);
        const ur = toScene(wp.upperRight[0], wp.upperRight[1], wp.upperRight[2]);
        const feet = ll.clone().add(lr).multiplyScalar(0.5); // lower-centre of the quad
        const top = ul.clone().add(ur).multiplyScalar(0.5);
        const height = Math.max(1.4, top.y - feet.y);
        const mannequin = buildMannequin(height);
        mannequin.position.set(feet.x, 0, feet.z); // stand on the pitch at the quad centre
        scene.add(mannequin);
      }
    }

    // ── Camera: placed "behind the kicker" on the goal→kick line extended ──
    const kx = traj.kickLocation.x;
    const kz = traj.kickLocation.z;
    const kickGroundDist = Math.hypot(kx, kz) || 1;
    const dirX = kx / kickGroundDist;
    const dirZ = kz / kickGroundDist;
    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
    const clampCameraY = () => {
      camera.position.y = Math.max(CAMERA_MIN_Y_M, camera.position.y);
    };
    const goalCenter = toScene(0, GOAL_HEIGHT / 2, 0);
    const cameraBehind = Math.max(5, 6 + kickGroundDist * 0.15);
    const cameraHeight = Math.max(3.5, 2.2 + kickGroundDist * 0.08);

    let orbitTarget = kickPos.clone().setY(GOAL_HEIGHT * 0.4);
    // Always in fly mode: switching kicks keeps the camera where the user flew it, and picking a camera preset
    // (the only thing that changes `cameraPreset`) moves the camera to that preset.
    const rememberedCamera = cameraMemoryRef.current;
    if (rememberedCamera && rememberedCamera.cameraPreset === cameraPreset) {
      camera.position.copy(rememberedCamera.position);
      camera.quaternion.copy(rememberedCamera.quaternion);
      orbitTarget = rememberedCamera.target.clone();
    } else if (cameraPreset === 'kickLocation') {
      const camX = clamp(kx + dirX * cameraBehind, 6, PITCH_EXTENT_X - 2);
      const camZ = clamp(kz + dirZ * cameraBehind, -PITCH_EXTENT_Z + 2, PITCH_EXTENT_Z - 2);
      camera.position.copy(toScene(camX, cameraHeight, camZ));
      camera.lookAt(kickPos.clone().lerp(goalCenter, 0.5));
    } else if (cameraPreset === 'penaltyBox') {
      // Extra-far view from behind the kick location, so camera distance is larger than
      // the reachable-region extent around the kick for corner scenarios.
      const farBehindKick = Math.max(20, kickGroundDist * 0.9);
      const camX = clamp(kx + dirX * farBehindKick, -4, PITCH_EXTENT_X + 8);
      const camZ = clamp(kz + dirZ * farBehindKick, -PITCH_EXTENT_Z - 8, PITCH_EXTENT_Z + 8);
      camera.position.copy(toScene(camX, 4.2, camZ));
      orbitTarget = new THREE.Vector3().copy(kickPos).lerp(goalCenter, 0.55).setY(2.0);
      camera.lookAt(orbitTarget);
    } else {
      // Explicit keeper origin view (soccer-space 0,0,0).
      camera.position.copy(toScene(0, 0, 0));
      orbitTarget = kickPos.clone().setY(Math.max(1.55, GOAL_HEIGHT * 0.48));
      camera.lookAt(orbitTarget);
    }
    clampCameraY();

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(orbitTarget);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 3;
    controls.maxDistance = 80;
    controls.maxPolarAngle = Math.PI / 2 - 0.05;
    controls.update();

    const flyMoveState = {
      forward: false,
      back: false,
      left: false,
      right: false,
      up: false,
      down: false,
      pitchUp: false,
      pitchDown: false,
      yawLeft: false,
      yawRight: false,
    };
    let flyDragging = false;
    let flyPointerId: number | null = null;
    let flyLastPointer: { x: number; y: number } | null = null;

    const clampPitch = (pitch: number): number => Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
    const applyFlyLook = (yawDelta: number, pitchDelta: number) => {
      const euler = new THREE.Euler(0, 0, 0, 'YXZ');
      euler.setFromQuaternion(camera.quaternion);
      euler.y += yawDelta;
      euler.x = clampPitch(euler.x + pitchDelta);
      camera.quaternion.setFromEuler(euler);
    };

    const onFlyPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      flyDragging = true;
      flyPointerId = e.pointerId;
      flyLastPointer = { x: e.clientX, y: e.clientY };
      renderer.domElement.setPointerCapture(e.pointerId);
      renderer.domElement.style.cursor = 'grabbing';
      e.preventDefault();
    };
    const onFlyPointerMove = (e: PointerEvent) => {
      if (!flyDragging || !flyLastPointer) return;
      const dx = e.movementX || (e.clientX - flyLastPointer.x);
      const dy = e.movementY || (e.clientY - flyLastPointer.y);
      flyLastPointer = { x: e.clientX, y: e.clientY };
      applyFlyLook(-dx * FLY_MOUSE_LOOK_SENSITIVITY, -dy * FLY_MOUSE_LOOK_SENSITIVITY);
    };
    const stopFlyDrag = () => {
      if (flyPointerId !== null) {
        try {
          renderer.domElement.releasePointerCapture(flyPointerId);
        } catch {
          // Ignore: pointer may already be released.
        }
      }
      flyDragging = false;
      flyPointerId = null;
      flyLastPointer = null;
      renderer.domElement.style.cursor = '';
    };

    const onFlyKeyDown = (e: KeyboardEvent) => {
      switch (e.code) {
        case 'KeyW': flyMoveState.forward = true; break;
        case 'KeyS': flyMoveState.back = true; break;
        case 'KeyA': flyMoveState.left = true; break;
        case 'KeyD': flyMoveState.right = true; break;
        case 'KeyR': case 'KeyQ': flyMoveState.up = true; break;
        case 'KeyF': case 'KeyE': flyMoveState.down = true; break;
        case 'ArrowUp': flyMoveState.pitchUp = true; break;
        case 'ArrowDown': flyMoveState.pitchDown = true; break;
        case 'ArrowLeft': flyMoveState.yawLeft = true; break;
        case 'ArrowRight': flyMoveState.yawRight = true; break;
        default: return;
      }
      e.preventDefault();
    };
    const onFlyKeyUp = (e: KeyboardEvent) => {
      switch (e.code) {
        case 'KeyW': flyMoveState.forward = false; break;
        case 'KeyS': flyMoveState.back = false; break;
        case 'KeyA': flyMoveState.left = false; break;
        case 'KeyD': flyMoveState.right = false; break;
        case 'KeyR': case 'KeyQ': flyMoveState.up = false; break;
        case 'KeyF': case 'KeyE': flyMoveState.down = false; break;
        case 'ArrowUp': flyMoveState.pitchUp = false; break;
        case 'ArrowDown': flyMoveState.pitchDown = false; break;
        case 'ArrowLeft': flyMoveState.yawLeft = false; break;
        case 'ArrowRight': flyMoveState.yawRight = false; break;
        default: return;
      }
      e.preventDefault();
    };

    renderer.domElement.addEventListener('pointerdown', onFlyPointerDown);
    renderer.domElement.addEventListener('pointermove', onFlyPointerMove);
    renderer.domElement.addEventListener('pointerup', stopFlyDrag);
    renderer.domElement.addEventListener('pointercancel', stopFlyDrag);
    renderer.domElement.addEventListener('pointerleave', stopFlyDrag);
    window.addEventListener('pointerup', stopFlyDrag);
    window.addEventListener('keydown', onFlyKeyDown, { passive: false });
    window.addEventListener('keyup', onFlyKeyUp, { passive: false });

    const preventDefaultFlightKeys = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'q', 'e', 'r', 'f', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', preventDefaultFlightKeys, { passive: false });

    // ── Animation loop ──
    const totalT = totalDuration(traj);
    setDuration(totalT);
    const pauseAfter = 0.5;
    let flightT = 0;
    let pauseT = 0;
    let lastFrame = performance.now();
    let lastUiSync = 0;
    let rafId = 0;
    let disposed = false;

    function tick() {
      if (disposed) return;
      const now = performance.now();
      const dt = Math.min((now - lastFrame) / 1000, 0.1);
      lastFrame = now;

      if (seekRef.current !== null) {
        flightT = Math.max(0, Math.min(totalT, seekRef.current));
        pauseT = 0;
        seekRef.current = null;
      } else if (isPlayingRef.current) {
        if (flightT < totalT) {
          flightT = Math.min(totalT, flightT + dt * speedRef.current);
        } else {
          pauseT += dt;
          if (pauseT >= pauseAfter) { flightT = 0; pauseT = 0; }
        }
      }
      ball.position.copy(positionAtTime(traj, flightT));

      if (now - lastUiSync > 33) { lastUiSync = now; setPlaybackTime(flightT); }

      {
        controls.enabled = false;
        const moveX = (flyMoveState.right ? 1 : 0) - (flyMoveState.left ? 1 : 0);
        const moveY = (flyMoveState.up ? 1 : 0) - (flyMoveState.down ? 1 : 0);
        const moveZ = (flyMoveState.back ? 1 : 0) - (flyMoveState.forward ? 1 : 0);
        const moveDelta = dt * FLY_MOVE_SPEED_MPS;
        if (moveX !== 0) camera.translateX(moveX * moveDelta);
        if (moveY !== 0) camera.translateY(moveY * moveDelta);
        if (moveZ !== 0) camera.translateZ(moveZ * moveDelta);
        clampCameraY();

        const yawAxis = (flyMoveState.yawLeft ? 1 : 0) - (flyMoveState.yawRight ? 1 : 0);
        const pitchAxis = (flyMoveState.pitchUp ? 1 : 0) - (flyMoveState.pitchDown ? 1 : 0);
        if (yawAxis !== 0 || pitchAxis !== 0) {
          applyFlyLook(
            yawAxis * dt * FLY_KEY_LOOK_SPEED_RAD_PER_SEC,
            pitchAxis * dt * FLY_KEY_LOOK_SPEED_RAD_PER_SEC,
          );
        }

        const forward = new THREE.Vector3();
        camera.getWorldDirection(forward);
        controls.target.copy(camera.position.clone().addScaledVector(forward, 6));
      }
      controls.update();
      clampCameraY();

      let hoveredTrajectoryId: string | null = null;
      let hoveredShadowId: string | null = null;
      if (pointerInside) {
        raycaster.setFromCamera(pointer, camera);
        if (showOthersRef.current && otherEntries.length > 0) {
          const hits = raycaster.intersectObjects(otherEntries.map((e) => e.baseMesh), false);
          if (hits.length > 0) hoveredTrajectoryId = (hits[0].object.userData.kickId as string) ?? null;
        }
        if (reachableShadowEntries.length > 0) {
          const shadowHits = raycaster.intersectObjects(reachableShadowEntries.map((e) => e.mesh), false);
          if (shadowHits.length > 0) hoveredShadowId = (shadowHits[0].object.userData.kickId as string) ?? null;
        }
      }

      for (const entry of otherEntries) {
        const isTrajectoryHover = entry.id === hoveredTrajectoryId;
        const isShadowHover = entry.id === hoveredShadowId;
        const showReachable = isTrajectoryHover || isShadowHover;
        entry.material.opacity = isTrajectoryHover ? OTHER_HOVER_OPACITY : entry.defaultOpacity;
        entry.material.emissiveIntensity = isTrajectoryHover ? 0.6 : 0.15;
        // Reachable overlays on sibling trajectories render on trajectory hover or matching shadow hover.
        for (const reachableMesh of entry.reachableMeshes) {
          reachableMesh.visible = showReachable;
          const m = reachableMesh.material as THREE.MeshStandardMaterial;
          m.opacity = showReachable ? OTHER_HOVER_OPACITY : entry.defaultOpacity * 1.4;
          m.emissiveIntensity = showReachable ? 0.7 : 0.4;
        }
      }
      for (const entry of reachableShadowEntries) {
        const isHover = entry.kickId === hoveredShadowId;
        entry.material.opacity = isHover ? SHADOW_HOVER_OPACITY : entry.defaultOpacity;
      }
      if (!flyDragging) renderer.domElement.style.cursor = (hoveredTrajectoryId || hoveredShadowId) ? 'pointer' : '';

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    }
    rafId = requestAnimationFrame(tick);

    const ro = new ResizeObserver(() => {
      if (!mount) return;
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(mount);

    return () => {
      cameraMemoryRef.current = {
        position: camera.position.clone(),
        quaternion: camera.quaternion.clone(),
        target: controls.target.clone(),
        kickLocation: { x: traj.kickLocation.x, z: traj.kickLocation.z },
        cameraPreset,
      };
      disposed = true;
      cancelAnimationFrame(rafId);
      ro.disconnect();
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      renderer.domElement.removeEventListener('click', onClick);
      renderer.domElement.removeEventListener('pointerdown', onFlyPointerDown);
      renderer.domElement.removeEventListener('pointermove', onFlyPointerMove);
      renderer.domElement.removeEventListener('pointerup', stopFlyDrag);
      renderer.domElement.removeEventListener('pointercancel', stopFlyDrag);
      renderer.domElement.removeEventListener('pointerleave', stopFlyDrag);
      window.removeEventListener('pointerup', stopFlyDrag);
      window.removeEventListener('keydown', onFlyKeyDown);
      window.removeEventListener('keyup', onFlyKeyUp);
      window.removeEventListener('keydown', preventDefaultFlightKeys);
      controls.dispose();
      renderer.dispose();
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry.dispose();
        const mat = (obj as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
        else if (mat) mat.dispose();
      });
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, [trajectory, otherTrajectories, reachableRegionSegments, wallPlayers, targets, cameraPreset]);

  const handleToggleFullscreen = async () => {
    const root = rootRef.current;
    if (!root) return;

    if (document.fullscreenElement === root) {
      await document.exitFullscreen();
      return;
    }

    await root.requestFullscreen();
  };

  return (
    <div className={`ball-trajectory-3d${isFullscreen ? ' is-fullscreen' : ''}`} ref={rootRef}>
      <div className='ball-trajectory-3d__canvas' ref={mountRef} />
      <div className='ball-trajectory-3d__overlay'>
        {!compact && (
          <div className='ball-trajectory-3d__camera'>
            <button
              type='button'
              className={`ball-trajectory-3d__chip ${cameraPreset === 'kickLocation' ? 'is-active' : ''}`}
              onClick={() => setCameraPreset('kickLocation')}
            >
              Kick location
            </button>
            <button
              type='button'
              className={`ball-trajectory-3d__chip ${cameraPreset === 'penaltyBox' ? 'is-active' : ''}`}
              onClick={() => setCameraPreset('penaltyBox')}
            >
              Penalty box
            </button>
            <button
              type='button'
              className={`ball-trajectory-3d__chip ${cameraPreset === 'goalkeeper' ? 'is-active' : ''}`}
              onClick={() => setCameraPreset('goalkeeper')}
            >
              Goalkeeper
            </button>
          </div>
        )}
        <button
          type='button'
          className='ball-trajectory-3d__btn ball-trajectory-3d__btn--fullscreen'
          onClick={handleToggleFullscreen}
          aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          {isFullscreen ? '⤡' : '⤢'}
        </button>
        <button
          type='button'
          className={`ball-trajectory-3d__btn ball-trajectory-3d__btn--gear ${settingsOpen ? 'is-active' : ''}`}
          onClick={() => setSettingsOpen((o) => !o)}
          aria-label={t('players.trajectory3DSceneSettings')}
          title={t('players.trajectory3DSceneSettings')}
        >
          ⚙
        </button>
        {settingsOpen && (
          <div className='ball-trajectory-3d__settings' role='menu'>
            <div className='ball-trajectory-3d__settings-section'>
              <div className='ball-trajectory-3d__settings-label'>{t('players.trajectory3DGoalNet')}</div>
              <label className='ball-trajectory-3d__toggle'>
                <input
                  type='checkbox'
                  checked={showNet}
                  onChange={(e) => setShowNet(e.target.checked)}
                />
                <span>{showNet ? t('players.trajectory3DVisible') : t('players.trajectory3DHidden')}</span>
              </label>
            </div>
            {(otherTrajectories?.length ?? 0) > 0 && (
              <div className='ball-trajectory-3d__settings-section'>
                <div className='ball-trajectory-3d__settings-label'>{t('players.trajectory3DSessionTrajectories')}</div>
                <label className='ball-trajectory-3d__toggle'>
                  <input
                    type='checkbox'
                    checked={showOthers}
                    onChange={(e) => setShowOthers(e.target.checked)}
                  />
                  <span>
                    {showOthers
                      ? t('players.trajectory3DShowing', { count: otherTrajectories!.length })
                      : t('players.trajectory3DHidden')}
                  </span>
                </label>
              </div>
            )}
            <div className='ball-trajectory-3d__settings-section'>
              <div className='ball-trajectory-3d__settings-label'>Playback speed</div>
              <div className='ball-trajectory-3d__settings-options'>
                {[0.25, 0.5, 1, 2].map((s) => (
                  <button
                    key={s}
                    type='button'
                    className={`ball-trajectory-3d__chip ${speed === s ? 'is-active' : ''}`}
                    onClick={() => setSpeed(s)}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {!compact && (
        <div className='ball-trajectory-3d__legend'>
          <span><i style={{ background: trajectory?.color ?? '#ffb800' }} /> {t('players.trajectory3DLegendTrajectory')}</span>
          <span><i style={{ background: '#00e5ff' }} /> {t('players.trajectory3DLegendKickLocation')}</span>
          {trajectory?.goalCrossing && (
            <span><i style={{ background: '#00ff88' }} /> {t('players.trajectory3DLegendGoalCrossing')}</span>
          )}
        </div>
      )}
      <div className='ball-trajectory-3d__scrubber'>
        <button
          type='button'
          className='ball-trajectory-3d__btn ball-trajectory-3d__btn--playback'
          onClick={() => setIsPlaying((p) => !p)}
          aria-label={isPlaying ? t('players.trajectory3DPause') : t('players.trajectory3DPlay')}
        >
          {isPlaying ? (compact ? '⏸' : `⏸ ${t('players.trajectory3DPause')}`) : (compact ? '▶' : `▶ ${t('players.trajectory3DPlay')}`)}
        </button>
        <span className='ball-trajectory-3d__time'>
          {playbackTime.toFixed(2)}s / {duration.toFixed(2)}s
        </span>
        <input
          type='range'
          min={0}
          max={Math.max(0.001, duration)}
          step={0.01}
          value={Math.min(playbackTime, duration)}
          onChange={(e) => {
            const t2 = parseFloat(e.target.value);
            seekRef.current = t2;
            setPlaybackTime(t2);
            if (isPlaying) setIsPlaying(false);
          }}
          aria-label='Playback time'
        />
      </div>
      {!compact && (
        <div className='ball-trajectory-3d__hint'>
          {t('players.trajectory3DHint')} | WASD move | Q/E up/down | Arrow keys or mouse drag to look around
        </div>
      )}
    </div>
  );
};

export default BallTrajectory3DChart;
